import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/services/apiClient';

/**
 * The technician's worklist, from the technician-tasks module. Every response
 * is `{ success, data }` — unwrapped here, once.
 */

export type Lane = 'ready' | 'waiting' | 'with_you' | 'done' | 'tomorrow';
export type TaskState = 'open' | 'in_progress' | 'done' | 'cant';
export type CantReason = 'left' | 'machine_down' | 'cant_cooperate' | 'prior_step' | 'declined' | 'other';

export interface Person { id: string; firstName: string; lastName: string }

export interface Eye { sphere?: number | null; cylinder?: number | null; axis?: number | null }

export interface Device {
  id: string;
  name: string;
  room: string | null;
  kinds: string[];
  status: 'online' | 'down';
  downAt: string | null;
  downNote: string | null;
}

export interface TechTask {
  id: string;
  title: string;
  kind: string;
  kindLabel: string;
  /** A values form (refraction) — typed at the machine, no file. */
  valuesForm: boolean;
  sequence: 'before_dilation' | 'after_drops' | null;
  laterality: string | null;
  priority: 'routine' | 'urgent' | 'stat';
  indication: string | null;
  state: TaskState;
  lane: Lane;
  waiting: { until: string | null; label: string } | null;
  patient: (Person & { age: number | null; sex: 'F' | 'M' | null; mrn: string | null; newPatient: boolean }) | null;
  /** Checked in today — null when there is no booking today to say. */
  arrived: boolean | null;
  testOnly: boolean;
  visitOpen: boolean;
  requestedBy: Person | null;
  referrerName: string | null;
  takesMin: number | null;
  scheduledFor: string | null;
  flow: { name: string; step: number; steps: number; next: { label: string; role: string } | null } | null;
  bundle: { key: string; index: number; size: number; done: number } | null;
  assignee: Person | null;
  mine: boolean;
  startedAt: string | null;
  completedAt: string | null;
  completedBy: Person | null;
  closedElsewhere: boolean;
  cantReason: CantReason | null;
  cantNote: string | null;
  handedFrom: Person | null;
  handedAt: string | null;
  repeatOfId: string | null;
  repeatReason: string | null;
  note: string | null;
  values: { od?: Eye; os?: Eye } | null;
  captures: Array<{ id: string; name: string | null; mimeType: string | null; kind: string | null; status: string | null }>;
  externalRaised: boolean;
  identityMismatches: number;
  device: Device | null;
  medicalRecordId: string | null;
  sourceType: 'imaging_order' | 'flow_step' | 'repeat';
  createdAt: string;
}

export interface PriorStudy {
  date: string | null;
  title: string | null;
  attachments: Array<{ id: string; name: string; mimeType: string; kind: string; sizeBytes: number | null }>;
}

export interface Worklist {
  tasks: TechTask[];
  counts: Record<Lane | 'all', number>;
  devices: Device[];
}

export interface MyDay {
  today: { performed: number; retakes: number; pending: number; avgMinutes: number | null };
  month: { performed: number; retakes: number; avgMinutes: number | null };
  byKind: Array<{ kind: string; label: string; today: number; month: number; ratePercent: number | null; earnedMonth: number | null }>;
  rates: Array<{ kind: string; label: string; percent: number }>;
}

export type PerformedRow = TechTask & { price: number | null; ratePercent: number | null; commission: number | null };

export interface Receiver extends Person { role: string; lastActiveAt: string | null; presentToday: boolean }

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const unwrap = <T,>(res: { data: any }): T => (res.data?.data ?? res.data) as T;

export const TECH_KEY = ['technician'] as const;

export function useWorklist() {
  return useQuery({
    queryKey: [...TECH_KEY, 'worklist'],
    queryFn: async () => unwrap<Worklist>(await apiClient.get('/technician-tasks/worklist')),
    /* Orders publish the moment a doctor signs — a stale list is the one thing
       this screen cannot be. */
    refetchInterval: 30_000,
  });
}

export function useTechTask(id: string | undefined) {
  return useQuery({
    queryKey: [...TECH_KEY, 'task', id],
    enabled: Boolean(id),
    queryFn: async () => unwrap<TechTask & { prior: PriorStudy | null }>(await apiClient.get(`/technician-tasks/${id}`)),
  });
}

export function useMyDay() {
  return useQuery({
    queryKey: [...TECH_KEY, 'my-day'],
    queryFn: async () => unwrap<MyDay>(await apiClient.get('/technician-tasks/my-day')),
  });
}

export function usePerformed(opts: { from?: string; to?: string; q?: string; enabled?: boolean }) {
  return useQuery({
    queryKey: [...TECH_KEY, 'performed', opts.from ?? '', opts.to ?? '', opts.q ?? ''],
    enabled: opts.enabled ?? true,
    queryFn: async () =>
      unwrap<PerformedRow[]>(
        await apiClient.get('/technician-tasks/performed', {
          params: { from: opts.from || undefined, to: opts.to || undefined, q: opts.q || undefined },
        }),
      ),
  });
}

export function useReceivers(enabled = true) {
  return useQuery({
    queryKey: [...TECH_KEY, 'receivers'],
    enabled,
    queryFn: async () => unwrap<Receiver[]>(await apiClient.get('/technician-tasks/receivers')),
  });
}

/** The server's message, not axios's "Request failed with status code 400". */
export function errorText(e: unknown): string {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const r = (e as any)?.response?.data;
  const msg = r?.error?.message ?? r?.message;
  return (Array.isArray(msg) ? msg.join(' · ') : msg) || (e as Error)?.message || 'That did not go through';
}

/** Every write refreshes the worklist and the task it touched. */
export function useTechActions() {
  const qc = useQueryClient();
  const onSuccess = () => {
    void qc.invalidateQueries({ queryKey: TECH_KEY });
  };
  const useM = <A,>(fn: (a: A) => Promise<unknown>) => useMutation({ mutationFn: fn, onSuccess });
  return {
    start: useM((id: string) => apiClient.patch(`/technician-tasks/${id}/start`)),
    notThisPatient: useM((id: string) => apiClient.patch(`/technician-tasks/${id}/not-this-patient`)),
    note: useM((a: { id: string; note: string }) => apiClient.patch(`/technician-tasks/${a.id}/note`, { note: a.note })),
    values: useM((a: { id: string; od?: Eye; os?: Eye }) =>
      apiClient.patch(`/technician-tasks/${a.id}/values`, { od: a.od, os: a.os })),
    send: useM((a: { id: string; note?: string }) =>
      apiClient.post(`/technician-tasks/${a.id}/send`, a.note !== undefined ? { note: a.note } : {})),
    cant: useM((a: { id: string; reason: CantReason; note?: string }) =>
      apiClient.patch(`/technician-tasks/${a.id}/cant`, { reason: a.reason, note: a.note || undefined })),
    handOver: useM((a: { toUserId: string; taskIds: string[] }) => apiClient.post('/technician-tasks/hand-over', a)),
    call: useM((id: string) => apiClient.post(`/technician-tasks/${id}/call`)),
    raiseExternal: useM((id: string) => apiClient.post(`/technician-tasks/${id}/raise-external`)),
    removeCapture: useM((a: { id: string; attachmentId: string }) =>
      apiClient.delete(`/technician-tasks/${a.id}/capture/${a.attachmentId}`)),
    deviceDown: useM((a: { id: string; note?: string }) =>
      apiClient.patch(`/technician-tasks/devices/${a.id}/down`, { note: a.note || undefined })),
    deviceUp: useM((id: string) => apiClient.patch(`/technician-tasks/devices/${id}/up`)),
    addDevice: useM((a: { name: string; room?: string; kinds: string[] }) => apiClient.post('/technician-tasks/devices', a)),
    flagLow: useM((line: string) => apiClient.post('/technician-tasks/flag-low', { line })),
  };
}

/** The machine's file, straight to storage by a presigned PUT, then onto the test. */
export async function captureFile(taskId: string, file: File): Promise<{ attachmentId: string }> {
  const mimeType = file.type || 'application/octet-stream';
  const presign = unwrap<{ attachmentId: string; uploadUrl: string; requiredHeaders: Record<string, string> }>(
    await apiClient.post(`/technician-tasks/${taskId}/capture/presign`, {
      originalName: file.name,
      mimeType,
      sizeBytes: file.size,
    }),
  );
  const put = await fetch(presign.uploadUrl, { method: 'PUT', headers: presign.requiredHeaders, body: file });
  if (!put.ok) throw new Error(`Upload failed (${put.status})`);
  const etag = put.headers.get('ETag')?.replace(/^"|"$/g, '') ?? undefined;
  return unwrap(await apiClient.post(`/technician-tasks/${taskId}/capture/${presign.attachmentId}/finalize`, { etag }));
}

/** A picture of a stored file, through the authenticated stream — never a portable URL. */
export async function attachmentBlobUrl(attachmentId: string): Promise<string> {
  const res = await apiClient.get(`/attachments/${attachmentId}/stream`, { responseType: 'blob' });
  return URL.createObjectURL(res.data as Blob);
}
